import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import SEOHead from "@/components/feature/SEOHead";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "91-video");

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryVideos = videos.filter((v) => v.category === activeSlug);

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${activeCategory.name} - 樱花视频`,
    description: activeCategory.description,
    url: `https://www.sakuramovie.com/category/${activeSlug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "樱花视频",
      url: "https://www.sakuramovie.com",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://www.sakuramovie.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: activeCategory.name,
        item: `https://www.sakuramovie.com/category/${activeSlug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - 樱花视频高清在线观看`}
        description={activeCategory.description}
        keywords={`樱花视频,${activeCategory.name},视频在线观看,高清免费,数字视频网站`}
        canonicalUrl={`/category/${activeSlug}`}
        ogType="website"
        schemaData={{ ...categorySchema, ...breadcrumbSchema }}
      />

      <section className="w-full px-4 md:px-6 py-8 md:py-12 max-w-screen-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
            {activeCategory.name}
          </h1>
          <p className="text-sm text-foreground-600">{activeCategory.description}</p>
        </div>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                activeSlug === cat.slug
                  ? "bg-primary-500 text-white"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {categoryVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categoryVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary-200"
                data-product-shop=""
              >
                <div className="relative w-full aspect-video overflow-hidden">
                  <img
                    src={video.cover}
                    alt={video.title}
                    title={`${video.title} - ${activeCategory.name} - 樱花视频`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-xs text-foreground-600 line-clamp-2 mb-3">
                    {video.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{video.views}次观看</span>
                    <span>{video.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-video-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-600 text-sm">该分类暂无视频内容，请浏览其他分类</p>
          </div>
        )}
      </section>
    </>
  );
}